import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { App, type FormInstance } from 'antd'
import {
  rentalOrderBatchCreateSchema,
  rentalOrderCreateSchema,
  rentalOrderDraftUpdateSchema,
  type RentalOrder
} from '@super-mega/server/contracts/order'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import type { RentalFormValues } from '@/features/order/rental-form.types.ts'
import type { RentalListSearch } from '@/features/order/rental-list-search.ts'
import { type RentalRoomRow, type RentalSiteRow } from '@/features/order/rental-resource-selector-modals.tsx'
import {
  createOrderEditSubmission,
  createOrderSubmissionKey,
  enterpriseSubmissionNotice
} from '@/features/order/utils/order-enterprise-selection.ts'
import { type EnterpriseSelection } from '@/features/service-enterprise/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { showOrderFormErrors } from '../utils/order-form-validation.ts'
import { buildRentalPayload } from '../utils/rental-payload.ts'

export function useRentalSubmission({
  form,
  batch = false,
  existingOrder,
  selectedSite,
  selectedRoom,
  enterprisePending,
  returnSearch
}: {
  batch?: boolean
  form: FormInstance<RentalFormValues>
  existingOrder?: RentalOrder
  selectedSite?: RentalSiteRow
  selectedRoom?: RentalRoomRow
  enterprisePending: boolean
  returnSearch: RentalListSearch
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const navigate = useNavigate()
  const { message, modal } = App.useApp()
  const [getSubmissionKey] = useState(createOrderSubmissionKey)
  const [editSubmission] = useState(() =>
    createOrderEditSubmission<Record<string, unknown> & { enterpriseSelection?: EnterpriseSelection }>()
  )
  const create = useMutation(trpc.order.rental.create.mutationOptions())
  const batchCreate = useMutation(trpc.order.rental.batchCreate.mutationOptions())
  const update = useMutation(trpc.order.rental.updateDraft.mutationOptions())
  const [submitting, setSubmitting] = useState(false)
  const isSaving = submitting || update.isPending || create.isPending || batchCreate.isPending
  const busy = isSaving
  const generateContract = useRef(false)
  const openContractPage = (rentalOrderId: number) => {
    const contractUrl = `/orders/rentals/contract/${rentalOrderId}`
    try {
      const contractTab = window.open(contractUrl, '_blank')
      if (contractTab) {
        contractTab.opener = null
        void message.success('订单已保存，合同页已在新标签页打开')
        return
      }
    } catch {
      // 保存和列表跳转已经完成；提供用户点击打开的入口。
    }
    modal.info({
      title: '订单已保存',
      content: '浏览器阻止了新标签页，请点击下方按钮打开合同编辑页。',
      okText: '打开合同编辑页',
      onOk: () => {
        window.open(contractUrl, '_blank', 'noopener,noreferrer')
      }
    })
    return false
  }

  const returnToRentalList = () => navigate({ to: '/orders/rentals', search: returnSearch })

  const save = async (values: RentalFormValues) => {
    if (enterprisePending) {
      void message.warning('请先确认或取消正在编辑的企业资料，再保存订单')
      return
    }
    if (!selectedSite) {
      showOrderFormErrors(form, [{ path: ['siteId'], message: '请选择租赁场地' }])
      void message.error('请选择租赁场地')
      return
    }
    if (!batch && values.occupancyMode === 'physical' && !selectedRoom) {
      showOrderFormErrors(form, [{ path: ['siteId'], message: '实际入驻必须选择房间' }])
      void message.error('实际入驻必须选择房间')
      return
    }
    if (values.orderGroupMode === 'existing' && !values.orderGroupId) {
      showOrderFormErrors(form, [{ path: ['orderGroupId'], message: '请选择已有订单组' }])
      void message.error('请选择已有订单组')
      return
    }
    try {
      const buildPayload = async () => {
        // Resolve explicit supplementation before the first attempt; unchanged retries must keep that exact request.
        const latestOrder = existingOrder
          ? await cache.fetchQuery({
              ...trpc.order.rental.detail.queryOptions({ rentalOrderId: existingOrder.id }),
              staleTime: 0
            })
          : undefined
        return buildRentalPayload({ values, selectedSite, selectedRoom, existingOrder, latestOrder })
      }
      const payload = existingOrder
        ? await editSubmission.prepare(
            { rentalOrderId: existingOrder.id, values, selectedSite, selectedRoom },
            async () => ({ ...(await buildPayload()), rentalOrderId: existingOrder.id })
          )
        : await buildPayload()
      if (batch) {
        const batchPayload = { ...payload, quantity: values.quantity }
        const parsed = rentalOrderBatchCreateSchema.safeParse({
          ...batchPayload,
          idempotencyKey: getSubmissionKey(batchPayload)
        })
        if (!parsed.success) {
          showOrderFormErrors(form, parsed.error.issues)
          void message.error(parsed.error.issues[0]?.message ?? '请检查批量租赁信息')
          return
        }
        const result = await batchCreate.mutateAsync(parsed.data)
        await Promise.all([
          cache.invalidateQueries(trpc.order.rental.list.queryFilter()),
          cache.invalidateQueries(trpc.order.groups.list.queryFilter()),
          cache.invalidateQueries(trpc.site.list.queryFilter()),
          cache.invalidateQueries(trpc.room.list.queryFilter()),
          cache.invalidateQueries(trpc.room.tree.queryFilter()),
          cache.invalidateQueries(trpc.room.recommend.queryFilter()),
          cache.invalidateQueries(trpc.room.availableOptions.queryFilter())
        ])
        void message.success(`已创建 ${result.orders.length} 个租赁订单，资料可稍后补齐`)
        await returnToRentalList()
        return
      }
      const notice = enterpriseSubmissionNotice([
        'enterpriseSelection' in payload ? payload.enterpriseSelection : undefined
      ])
      if (
        notice &&
        !(await modal.confirm({
          title: '确认保存订单及企业资料',
          content: notice,
          okText: '确认保存',
          cancelText: '继续编辑'
        }))
      ) {
        return
      }
      const parsed = existingOrder
        ? rentalOrderDraftUpdateSchema.safeParse(payload)
        : rentalOrderCreateSchema.safeParse({ ...payload, idempotencyKey: getSubmissionKey(payload) })
      if (!parsed.success) {
        if (existingOrder) {
          editSubmission.clear()
        }
        showOrderFormErrors(form, parsed.error.issues)
        void message.error(parsed.error.issues[0]?.message ?? '请检查租赁信息')
        return
      }
      const created = existingOrder
        ? await update.mutateAsync({ ...parsed.data, rentalOrderId: existingOrder.id })
        : await create.mutateAsync(parsed.data)
      if (existingOrder) {
        editSubmission.clear()
      }
      await Promise.all([
        refreshMoneyQueries(cache, trpc),
        cache.invalidateQueries(trpc.order.subjects.list.queryFilter()),
        cache.invalidateQueries(trpc.order.subjects.options.queryFilter()),
        cache.invalidateQueries(trpc.order.subjects.detail.queryFilter()),
        ...(notice
          ? [
              cache.invalidateQueries(trpc.company.reusable.queryFilter()),
              cache.invalidateQueries(trpc.company.search.queryFilter()),
              cache.invalidateQueries(trpc.identity.reusable.queryFilter()),
              cache.invalidateQueries(trpc.identity.search.queryFilter()),
              cache.invalidateQueries(trpc.license.list.queryFilter())
            ]
          : []),
        cache.invalidateQueries(trpc.order.groups.list.queryFilter())
      ])
      void message.success(existingOrder ? '租赁订单已更新' : '租赁订单已保存为待处理')
      await returnToRentalList()
      if (generateContract.current) {
        openContractPage(created.id)
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '租赁订单创建失败'))
    }
  }

  const onFinish = () => {
    if (submitting || enterprisePending) {
      return
    }
    setSubmitting(true)
    return save(form.getFieldsValue(true)).finally(() => {
      generateContract.current = false
      setSubmitting(false)
    })
  }
  const onFinishFailed = () => {
    generateContract.current = false
  }
  const saveWithContract = () => {
    if (isSaving || enterprisePending) {
      return
    }
    generateContract.current = true
    form.submit()
  }
  return { isSaving, busy, editSubmission, onFinish, onFinishFailed, saveWithContract }
}
